import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Upload, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { submitAdvert, uploadAdvertImage, validateDiscountCode } from "@/lib/api";

/**
 * Public page where an advertiser submits their finished ad creative
 * (uploaded or coming from /sponsor Advert Studio) + their company / billing details.
 *
 * Lands in /admin > Advert Queue → approve → live ad.
 */
const AdvertSubmitPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const presetImage = params.get("image") || params.get("advert_url") || "";
  const source = params.get("source") || (presetImage ? "advert-studio" : "manual");
  const auctionId = params.get("auction") || "";
  const presetCode = (params.get("code") || "").toUpperCase();

  const [form, setForm] = useState({
    image_url: presetImage,
    target_url: "",
    title: "",
    subtitle: "",
    format: "banner",
    placements: ["compare-top"],
    contact_name: "",
    company: "",
    contact_email: "",
    billing_email: "",
    address: "",
    country: "",
    vat_number: "",
    notes: "",
    discount_code: presetCode,
    auction_id: auctionId || null,
    source,
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [discount, setDiscount] = useState({ status: "idle", percent_off: 0, description: "" });

  useEffect(() => {
    if (presetImage) setForm((f) => ({ ...f, image_url: presetImage, source }));
  }, [presetImage, source]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setError("");
    try {
      const data = await uploadAdvertImage(f);
      setForm((s) => ({ ...s, image_url: data.url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const checkDiscount = async () => {
    const code = form.discount_code.trim();
    if (!code) { setDiscount({ status: "idle", percent_off: 0, description: "" }); return; }
    setDiscount({ status: "checking", percent_off: 0, description: "" });
    try {
      const d = await validateDiscountCode(code);
      setDiscount({ status: "valid", percent_off: d.percent_off, description: d.description || "" });
    } catch (_) {
      setDiscount({ status: "invalid", percent_off: 0, description: "" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.image_url) { setError(isNl ? "Voeg een advertentie-afbeelding toe of upload er één." : "Add an advert image (upload or AI-generate)."); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.auction_id) delete payload.auction_id;
      await submitAdvert(payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.detail || (isNl ? "Versturen mislukt" : "Submission failed"));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <main className="hac-container py-20 max-w-2xl text-center" data-testid="advert-submit-done">
        <CheckCircle2 size={56} className="mx-auto text-emerald-600 mb-4" />
        <h1 className="font-heading text-3xl text-[#0F172A] mb-3">{isNl ? "Bedankt! Je advertentie staat in de wachtrij." : "Thanks! Your ad is in the approval queue."}</h1>
        <p className="text-[#64748B] mb-6">
          {isNl
            ? "We controleren je advertentie binnen 24 uur. Na goedkeuring gaat hij direct live en sturen we de factuur naar het door jou opgegeven facturatie-emailadres."
            : "We'll review your ad within 24 hours. Once approved it goes live immediately and we send the invoice to the billing email you provided."}
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/sponsor" className="hac-btn-secondary">{isNl ? "Terug naar Adverteren" : "Back to Advertise"}</Link>
          <Link to="/" className="hac-btn-primary">{isNl ? "Naar de homepage" : "Back home"}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#F8FAFC] min-h-screen pb-20">
      <section className="hac-container py-12 max-w-3xl">
        <div className="inline-flex items-center gap-2 bg-violet-600 text-white px-3 py-1 rounded-full text-xs font-bold mb-3">
          <Sparkles size={12} /> {source === "auction" ? (isNl ? "Veiling-winnaar flow" : "Auction winner flow") : (isNl ? "Advertentie indienen" : "Submit your ad")}
        </div>
        <h1 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-2">
          {isNl ? "Plaats jouw advertentie op HorseAppCompare" : "Submit your ad to HorseAppCompare"}
        </h1>
        <p className="text-[#64748B] mb-8">
          {isNl
            ? "Vul je advertentie + bedrijfsgegevens in. Na goedkeuring door onze redactie (binnen 24u) staat hij live op de gekozen placement(s)."
            : "Add your creative + company details. After review (within 24h) it goes live on the chosen placement(s)."}
        </p>

        {source === "auction" && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="advert-auction-banner">
            <strong>{isNl ? "Veiling gewonnen 🎉" : "Auction won 🎉"}</strong> — {isNl
              ? "vul hieronder je advertentie aan en bevestig je bedrijfs- en facturatiegegevens. Na goedkeuring wordt jouw app als Featured app van de week getoond."
              : "complete your ad below and confirm your company + billing details. Once approved your app runs as Featured app of the week."}
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          {/* Creative */}
          <fieldset className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3" data-testid="advert-fieldset-creative">
            <legend className="px-2 text-xs font-bold uppercase tracking-widest text-[#64748B]">{isNl ? "Advertentie" : "Creative"}</legend>
            <div>
              <label className="overline block mb-1">{isNl ? "Afbeelding" : "Image"} *</label>
              {form.image_url ? (
                <div className="space-y-2">
                  <img src={form.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url} alt="ad preview" className="w-full rounded-lg border border-[#E2E8F0]" data-testid="advert-preview" />
                  <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-xs text-red-600 hover:underline" data-testid="advert-image-replace">{isNl ? "Vervang afbeelding" : "Replace image"}</button>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block border-2 border-dashed border-[#CBD5E1] hover:border-[#2563EB] rounded-xl p-6 text-center cursor-pointer">
                    <Upload size={20} className="mx-auto mb-1 text-[#64748B]" />
                    <span className="text-sm text-[#0F172A] font-medium">{uploading ? (isNl ? "Bezig met uploaden…" : "Uploading…") : (isNl ? "Upload je advertentie (PNG/JPG, max 8MB)" : "Upload your ad (PNG/JPG, max 8MB)")}</span>
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUpload} disabled={uploading} data-testid="advert-upload-input" />
                  </label>
                  <p className="text-xs text-[#64748B]">{isNl ? "Of " : "Or "}<Link to="/sponsor#advert-studio-section" className="text-violet-600 hover:underline font-medium">{isNl ? "genereer een advertentie via de Advert Studio →" : "generate one via the Advert Studio →"}</Link></p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">{isNl ? "Titel (zichtbaar)" : "Headline (visible)"} *</label>
                <input className="hac-input" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="advert-title" />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Doel-URL" : "Target URL"} *</label>
                <input type="url" className="hac-input" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} required placeholder="https://" data-testid="advert-target" />
              </div>
            </div>
            <div>
              <label className="overline block mb-1">{isNl ? "Ondertitel (optioneel)" : "Subtitle (optional)"}</label>
              <input className="hac-input" value={form.subtitle} maxLength={200} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="advert-subtitle" />
            </div>
            <div>
              <label className="overline block mb-1">{isNl ? "Formaat" : "Format"}</label>
              <select className="hac-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="advert-format">
                <option value="banner">{isNl ? "Banner 3:1" : "Banner 3:1"}</option>
                <option value="square">{isNl ? "Vierkant 1:1" : "Square 1:1"}</option>
              </select>
            </div>
          </fieldset>

          {/* Contact */}
          <fieldset className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3" data-testid="advert-fieldset-contact">
            <legend className="px-2 text-xs font-bold uppercase tracking-widest text-[#64748B]">{isNl ? "Contact" : "Contact"}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">{isNl ? "Naam contactpersoon" : "Contact name"} *</label>
                <input className="hac-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} required data-testid="advert-contact-name" />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Email contactpersoon" : "Contact email"} *</label>
                <input type="email" className="hac-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} required data-testid="advert-contact-email" />
              </div>
            </div>
          </fieldset>

          {/* Billing */}
          <fieldset className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3" data-testid="advert-fieldset-billing">
            <legend className="px-2 text-xs font-bold uppercase tracking-widest text-[#64748B]">{isNl ? "Bedrijfs- en facturatiegegevens" : "Company & billing"}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">{isNl ? "Bedrijfsnaam" : "Company name"} *</label>
                <input className="hac-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required data-testid="advert-company" />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Email voor factuur" : "Billing email"}</label>
                <input type="email" className="hac-input" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} placeholder={form.contact_email || (isNl ? "leeg = contact email" : "leave empty = contact email")} data-testid="advert-billing-email" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-1">{isNl ? "Adres" : "Address"}</label>
                <input className="hac-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="advert-address" />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Land" : "Country"}</label>
                <input className="hac-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="advert-country" />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "BTW-nummer (optioneel)" : "VAT number (optional)"}</label>
                <input className="hac-input" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid="advert-vat" />
              </div>
            </div>
          </fieldset>

          {/* Discount */}
          <fieldset className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-2" data-testid="advert-fieldset-discount">
            <legend className="px-2 text-xs font-bold uppercase tracking-widest text-[#64748B]">{isNl ? "Kortingscode (optioneel)" : "Discount code (optional)"}</legend>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="overline block mb-1">{isNl ? "Code" : "Code"}</label>
                <input className="hac-input uppercase" value={form.discount_code} onChange={(e) => { setForm({ ...form, discount_code: e.target.value.toUpperCase() }); setDiscount({ status: "idle", percent_off: 0, description: "" }); }} placeholder="WELCOME20" data-testid="advert-discount-input" />
              </div>
              <button type="button" onClick={checkDiscount} className="hac-btn-secondary" data-testid="advert-discount-apply">{discount.status === "checking" ? <Loader2 size={14} className="animate-spin" /> : (isNl ? "Toepassen" : "Apply")}</button>
            </div>
            {discount.status === "valid" && <p className="text-xs text-emerald-700" data-testid="advert-discount-valid"><strong>{discount.percent_off}% {isNl ? "korting toegepast" : "discount applied"}</strong>{discount.description ? ` — ${discount.description}` : ""}</p>}
            {discount.status === "invalid" && <p className="text-xs text-red-600" data-testid="advert-discount-invalid">{isNl ? "Code ongeldig of verlopen." : "Code invalid or expired."}</p>}
          </fieldset>

          {/* Notes */}
          <fieldset className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="advert-fieldset-notes">
            <label className="overline block mb-1">{isNl ? "Opmerkingen (optioneel)" : "Notes (optional)"}</label>
            <textarea className="hac-input min-h-[80px]" value={form.notes} maxLength={1000} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={isNl ? "Specifieke wensen voor placement, start-datum, etc." : "Specific wishes for placement, start date, etc."} data-testid="advert-notes" />
          </fieldset>

          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="advert-error">{error}</div>}

          <button type="submit" disabled={submitting} className="hac-btn-primary w-full justify-center text-base py-3" data-testid="advert-submit">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : (isNl ? "Verstuur advertentie ter goedkeuring" : "Submit ad for approval")} <ArrowRight size={14} />
          </button>
          <p className="text-[11px] text-[#94A3B8] text-center">{isNl ? "We controleren binnen 24u en sturen na goedkeuring de factuur." : "We review within 24h and send the invoice after approval."}</p>
        </form>
      </section>
    </main>
  );
};

export default AdvertSubmitPage;
